import { debounce, isEqual } from 'lodash';
import { Config, Widget, OptionsState, WindowExtended } from '../types';
import { STEPS, OVERLAY_PLACEMENTS, EXPORT_QUALITIES, EXPORT_FORMATS, WIDGET_UPDATE_DEBOUNCE_WAIT_TIME, TEXT_PRESETS } from '../constants/general';
import { setExported } from '../actions';
import { store } from '../store';
import { getCldImageUrl } from '../utils';

export const refreshWidget = (widget: Widget) => {
  widget.hide();
  widget.show();
};

export const initializeWidget = (window: WindowExtended) => {
  const widget = window.cloudinary.mediaEditor({ appendTo: document.getElementById('widget_container') });
  widget.on('export', (data) => {
    store.dispatch(setExported({ isExported: true, url: data.assets[0].url }));
  });
  return widget;
};

export const configToWidgetOptions = (config: Config, options: OptionsState) => {
  const selectedTheme = config.themes[options.selectedThemeIndex];
  const selectedThemePath = `${config.themesPath}/${selectedTheme.name}`;

  const selectedLogo = selectedTheme.logos[options.selectedLogoIndex];
  const selectedAsset = selectedTheme.assets[options.selectedAssetIndex];

  const steps = STEPS.filter((step) => options.activeSteps[step]);

  const presets = options.presetsOrder
    .filter((preset) => options.selectedPresets[preset])
    .map((preset) => (preset === 'custom' ? options.customPreset.params : preset));

  const placementOptions = OVERLAY_PLACEMENTS.filter((overlayPlacement) => options.selectedOverlayPlacements[overlayPlacement]);

  const overlays = selectedTheme.overlays
    .filter((overlayName, index) => !options.unselectedOverlayIndexes[index])
    .map((overlayName) => ({
      publicId: `${selectedThemePath}/overlays/${overlayName}`,
      label: overlayName,
      transformation: [],
      placementOptions,
    }));

  const exportQualities = EXPORT_QUALITIES.filter((quality) => options.selectedExportQualities[quality]);
  const exportFormats = EXPORT_FORMATS.filter((format) => options.selectedExportFormats[format]);
  const textPresets = TEXT_PRESETS.filter((preset) => options.textPresets[preset]);

  return {
    mode: 'inline',
    publicIds: [`${selectedThemePath}/assets/${selectedAsset}`],
    cloudName: config.cloudName,
    image: {
      steps,
      resizeAndCrop: {
        toggleAspectRatio: options.toggleAspectRatio,
        aspectRatioLock: options.aspectRatioLock,
        flip: options.flip,
        rotate: options.rotate,
        presets,
      },
      imageOverlay: {
        overlays,
      },
      textOverlays: {
        presets: textPresets,
      },
      export: {
        quality: exportQualities,
        formats: exportFormats,
        download: options.download,
        share: options.share,
      },
    },
    theme: {
      logo: getCldImageUrl(config.cloudName, '', `${selectedThemePath}/logos/${selectedLogo}`),
    },
  };
};

let previousWidgetOptions = {};

export const updateWidget = (widget: Widget, config: Config, options: OptionsState) => {
  const widgetOptions = configToWidgetOptions(config, options);
  /* istanbul ignore else */
  if (!isEqual(previousWidgetOptions, widgetOptions)) {
    previousWidgetOptions = widgetOptions;
    widget.update(widgetOptions);
    widget.show();
  }
  return widget;
};

export const updateWidgetDebounced = debounce(updateWidget, WIDGET_UPDATE_DEBOUNCE_WAIT_TIME);

export const goToFirstAvailableStep = (widget?: Widget) => {
  /* istanbul ignore else */
  if (widget) {
    // The only way to go to first step is to reset the widget like that
    refreshWidget(widget);
  }
};
